import type { UnitFiltersDTO } from 'src/api/models';
import { UnitFiltersDTOMatchMode } from 'src/api/models';
import { useUnitService } from 'src/services/unit-service';
import type {
  EntitySearchConfig,
  EntitySearchResult,
  UnitEntity,
  PaginationParams,
  PaginatedResponse,
} from '../types';
import { ENTITY_ICONS, getEntityDisplayName, getEntityDisplayProps } from 'src/components/ticket-edit/entity-display';

type StatusVariant = 'success' | 'danger' | 'warning' | 'info' | 'default';

function getStatusBadge(status?: string): { text: string; variant: StatusVariant } {
  const statusMap: Record<string, { text: string; variant: StatusVariant }> = {
    active: { text: 'Active', variant: 'success' },
    inactive: { text: 'Inactive', variant: 'danger' },
    maintenance: { text: 'Maintenance', variant: 'warning' },
    retired: { text: 'Retired', variant: 'default' },
  };

  return statusMap[status?.toLowerCase() || ''] || { text: status || 'Unknown', variant: 'default' };
}

export function createUnitSearchConfig(): EntitySearchConfig<UnitEntity> {
  const { fetchUnits, fetchUnitsByOrganisation } = useUnitService();

  return {
    entityType: 'unit',
    icon: ENTITY_ICONS.unit,
    getDisplayName: (entity) => getEntityDisplayName(entity, 'unit'),
    getDisplayProps: (entity) => getEntityDisplayProps(entity, 'unit'),

    searchFields: [
      { name: 'fleet_number', weight: 2 },
      { name: 'serial_number', weight: 2 },
      { name: 'name', weight: 1.5 },
      { name: 'description', weight: 1 },
    ],

    async fetchAll(pagination: PaginationParams): Promise<PaginatedResponse<UnitEntity>> {
      const response = await fetchUnits({}, pagination);
      return {
        data: response.data || [],
        hasMore: (response.page ?? 0) < (response.total_pages ?? 0),
        totalPages: response.total_pages,
      };
    },

    async fetchByParent(
      organisationId: string,
      _pagination: PaginationParams
    ): Promise<PaginatedResponse<UnitEntity>> {
      // fetchUnitsByOrganisation returns all units for an org (no pagination from API)
      const units = await fetchUnitsByOrganisation(organisationId);
      return {
        data: units,
        hasMore: false,
      };
    },

    async apiSearch(
      query: string,
      pagination: PaginationParams,
      excludeIds?: string[]
    ): Promise<PaginatedResponse<UnitEntity>> {
      const filters: UnitFiltersDTO = {
        match_mode: UnitFiltersDTOMatchMode.or,
        serial_number: query,
        name: query,
      };

      // Try to parse as fleet number (numeric)
      const fleetNumber = parseInt(query, 10);
      if (!isNaN(fleetNumber)) {
        filters.fleet_number = fleetNumber;
      }

      const response = await fetchUnits(filters, pagination);
      let data = response.data || [];

      // Exclude specified IDs
      if (excludeIds && excludeIds.length > 0) {
        const excludeSet = new Set(excludeIds);
        data = data.filter((unit) => unit.id && !excludeSet.has(unit.id));
      }

      return {
        data,
        hasMore: (response.page ?? 0) < (response.total_pages ?? 0),
        totalPages: response.total_pages,
      };
    },

    formatResult(unit: UnitEntity, isRelated?: boolean): EntitySearchResult<UnitEntity> {
      const statusBadge = getStatusBadge(unit.status);

      // Build subtitle - include org name for unrelated units
      const subtitleParts = [
        unit.serial_number && `S/N: ${unit.serial_number}`,
        unit.category?.name,
        unit.manufacture_name,
        unit.manufactured_year && `(${unit.manufactured_year})`,
      ].filter(Boolean);

      // Add org name to subtitle for unrelated units
      if (!isRelated && unit.organisation?.name) {
        subtitleParts.push(`@ ${unit.organisation.name}`);
      }

      return {
        id: unit.id || '',
        title: [unit.name, unit.fleet_number && `#${unit.fleet_number}`].filter(Boolean).join(' '),
        subtitle: subtitleParts.join(' - '),
        icon: 'pi-truck',
        badges: [statusBadge],
        meta: {
          isRelated,
          organisationId: unit.organisation?.id,
          organisationName: unit.organisation?.name,
        },
        originalItem: unit,
      };
    },

    getOrganisationId(unit: UnitEntity): string | undefined {
      return unit.organisation?.id;
    },
  };
}
